import { isArray } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import { stringFormat } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { CSSProperties, useRef, useState } from 'react';
import { useEffectCustom } from '../../../hooks/use-effect-custom';
import { KeyboardModal, KeyboardProps } from '../../../keyboard';
import { ModalDrawerProps } from '../../../modal-drawer';
import { getScrollParent } from '../../../utils/get-scroll-parent';
import { NativeProps } from '../../../utils/native-props';
import { getScrollPosition } from '../../../utils/scroll-position';
import './style.less';

export type VirtualInputProps = {
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  keyboardProps?: Omit<KeyboardProps, 'onInput' | 'onBackSpace' | 'onConfirm'>;
  modalProps?: Omit<ModalDrawerProps, 'visible' | 'className' | 'onClose' | 'bodyRef'>;
  maxLength?: number;
  autoConfirm?: boolean;
  value?: string;
  onChange?: (value: string, key?: string) => void;
} & NativeProps;

type VirtualInputPropsInner = VirtualInputProps & {
  name: string;
  format?: number[] | ((data?: string) => string);
};

/**
 * 文本框+数字键盘
 * 1. 内置键盘包括：纯数字键盘、金额键盘、身份证键盘
 * 2. 可自定义键盘键值，例如自定义车牌省份键盘
 * 3. 结合form使用最佳，结合form使用后value、onChange由form代理，单独设置无效
 *
 *
 * @param props
 * @returns
 */
export const VirtualInput = (props: VirtualInputProps) => {
  const newProps = props as VirtualInputPropsInner;
  const [visible, setVisible] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const keysRef = useRef<string[]>([]);
  const windowSize = hooks.useWindowSize();
  const scrollRef = useRef<Element | Window>();
  const scrollPaddingBottomRef = useRef<string>('');

  useEffectCustom(() => {
    keysRef.current = (newProps.value || '').split('');
  }, [newProps.value]);

  const onFocus = hooks.useCallbackRef(() => {
    setVisible(true);
    newProps.onFocus?.();
  });

  const onBlur = hooks.useCallbackRef(() => {
    setVisible(false);
    changeScrollStyle('close');
    newProps.onBlur?.();
  });

  const onClose = hooks.useCallbackRef(() => {
    rootRef.current?.blur();
  });

  const onInput = hooks.useCallbackRef((key: string) => {
    if (props.autoConfirm) {
      newProps.onChange?.(key);
      onClose();
      return;
    }
    const maxLength = props.maxLength;
    if (maxLength && maxLength > 0) {
      const currentValue = keysRef.current.join('');
      let formatValue = currentValue;
      if (typeof newProps.format === 'function') {
        formatValue = newProps.format(currentValue);
      } else if (isArray(newProps.format)) {
        formatValue = stringFormat(currentValue, newProps.format).join(' ');
      }

      if (formatValue.length >= maxLength) return;
    }
    keysRef.current.push(key);
    newProps.onChange?.(keysRef.current.join(''), key);
  });

  const onBackSpace = hooks.useCallbackRef(() => {
    const target = keysRef.current.pop();
    if (target) {
      newProps.onChange?.(keysRef.current.join(''));
    }
  });

  const onConfirm = hooks.useCallbackRef(() => {
    onClose();
  });

  const bodyRef = useRef<HTMLDivElement>();

  const bodyRefHandle = hooks.useCallbackRef((node) => {
    bodyRef.current = node;
  });

  const changeScrollStyle = (type: 'open' | 'close', style?: CSSProperties) => {
    if (type === 'open' && style) {
      if (scrollRef.current === window) {
        document.body.style.paddingBottom = style.paddingBottom as string;
      } else {
        (scrollRef.current as HTMLElement).style.paddingBottom = style.paddingBottom as string;
      }
    } else {
      if (scrollRef.current === window) {
        document.body.style.paddingBottom = scrollPaddingBottomRef.current;
      } else {
        (scrollRef.current as HTMLElement).style.paddingBottom = scrollPaddingBottomRef.current;
      }
    }
  };

  const onAfterOpen = hooks.useCallbackRef(() => {
    scrollRef.current = getScrollParent(rootRef.current as Element, window) as Element;
    const scrollTarget = scrollRef.current as HTMLElement | Window;

    const keyboardHeight = bodyRef.current?.offsetHeight || 0;
    const clientRect = rootRef.current?.getBoundingClientRect();
    const domClientBottom = (windowSize?.height || 0) - (clientRect?.bottom || 0);
    const diff = domClientBottom - keyboardHeight;
    const { top } = getScrollPosition(scrollTarget as Element);
    if (diff < 0) {
      if (scrollTarget === window) {
        scrollPaddingBottomRef.current = document.body.style.paddingBottom;
      } else {
        scrollPaddingBottomRef.current = (scrollTarget as HTMLElement).style.paddingBottom || '';
      }
      changeScrollStyle('open', { paddingBottom: `${keyboardHeight}px` });
      scrollTarget.scrollTo({
        top: top + Math.abs(diff) + (clientRect?.height || 0) + 60,
        behavior: 'smooth',
      });
    }
    props.modalProps?.onAfterOpen?.();
  });
  // const showValue =
  //   formatArray.length > 0 ? stringFormat(newProps.value || '', formatArray).join(' ') : newProps.value;

  let showValue = newProps.value || '';
  if (typeof newProps.format === 'function') {
    showValue = newProps.format(showValue);
  } else if (isArray(newProps.format)) {
    showValue = stringFormat(showValue, newProps.format).join(' ');
  }

  const mainClassName = classNames(
    'v-virtual-input',
    { 'v-virtual-disabled': newProps.disabled },
    { 'v-virtual-readonly': newProps.readOnly },
    props.className,
  );
  return (
    <div
      ref={rootRef}
      className={mainClassName}
      tabIndex={newProps.disabled || newProps.readOnly ? undefined : 0}
      onClick={newProps.onClick}
      onBlur={onBlur}
      onFocus={onFocus}
      style={props.style}
    >
      {!newProps.value ? (
        <span className="v-virtual-input-placeholder">{newProps.placeholder}</span>
      ) : (
        <span className="v-virtual-input-value">{showValue}</span>
      )}
      {visible && <span className="v-virtual-input-beat"></span>}
      <KeyboardModal
        keyboardProps={{
          ...props.keyboardProps,
          onInput,
          onBackSpace,
          onConfirm,
        }}
        visible={visible}
        onClose={onClose}
        title={props.modalProps?.title}
        closeIcon={props.modalProps?.closeIcon}
        modalProps={{
          ...props.modalProps,
          bodyRef: bodyRefHandle,
          onAfterOpen,
        }}
      />
    </div>
  );
};
VirtualInput.domTypeName = 'VirtualInput';
